export const normalizeHighlightName = (value) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9,]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const flippedName = (needle) => {
  const parts = needle.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2) {
    return normalizeHighlightName(`${parts[1]} ${parts[0]}`);
  }
  const tokens = needle.split(" ").filter(Boolean);
  if (tokens.length >= 2) {
    return `${tokens[tokens.length - 1]} ${tokens.slice(0, -1).join(" ")}`;
  }
  return "";
};

const nameMatches = (text, needle, flipped) =>
  Boolean(text) &&
  (text.includes(needle) || (flipped && text.includes(flipped)));

const looksLikeOtherPerson = (text, needle, flipped) => {
  const value = normalizeHighlightName(text);
  if (!value || nameMatches(value, needle, flipped)) return false;
  if (/^(name|avg|ave|gms|games|pins|hdcp|hcp|id)$/.test(value)) return false;
  const tokens = value.split(" ").filter((token) => /[a-z]{2,}/.test(token));
  return tokens.length >= 2 && tokens.length <= 5;
};

const unionBox = (items) => {
  if (!items.length) return null;
  const left = Math.min(...items.map((item) => item.left));
  const top = Math.min(...items.map((item) => item.top));
  const right = Math.max(...items.map((item) => item.right));
  const bottom = Math.max(...items.map((item) => item.bottom));
  if (right - left <= 0 || bottom - top <= 0) return null;
  return {
    topLeftX: left,
    topLeftY: top,
    bottomRightX: right,
    bottomRightY: bottom,
  };
};

const itemMidY = (item) => (item.top + item.bottom) / 2;

const groupItemsIntoLines = (items) => {
  const sorted = [...items].sort(
    (left, right) => itemMidY(left) - itemMidY(right) || left.left - right.left
  );
  const lines = [];
  sorted.forEach((item) => {
    const height = Math.max(item.bottom - item.top, 0.003);
    const line = lines.find(
      (entry) => Math.abs(entry.midY - itemMidY(item)) <= height * 0.65
    );
    if (line) {
      line.items.push(item);
      const mids = line.items.map(itemMidY);
      line.midY = mids.reduce((sum, value) => sum + value, 0) / mids.length;
    } else {
      lines.push({ midY: itemMidY(item), items: [item] });
    }
  });
  lines.forEach((line) => {
    line.items.sort((left, right) => left.left - right.left);
  });
  return lines;
};

const lineTextFromParts = (parts) =>
  parts
    .map((part) => part.norm)
    .filter(Boolean)
    .join(" ");

const wholeNumbersIn = (text) => text.match(/\d+/g) || [];

const lineHasNumber = (text, value) => {
  if (value == null || value === "") return false;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return false;
  const needle = String(Math.trunc(parsed));
  return wholeNumbersIn(text).includes(needle);
};

const boxMidY = (box) =>
  box ? (Number(box.topLeftY) + Number(box.bottomRightY)) / 2 : null;

const scoreNameLine = (text, hints = {}, lineMidY) => {
  let score = 0;
  if (lineHasNumber(text, hints.average)) score += 8;
  if (lineHasNumber(text, hints.games)) score += 4;
  if (lineHasNumber(text, hints.pins)) score += 4;
  score += Math.min(wholeNumbersIn(text).length, 8) * 0.5;
  const hintY = boxMidY(hints.boundingBox);
  if (Number.isFinite(hintY) && Number.isFinite(lineMidY)) {
    const distance = Math.abs(lineMidY - hintY);
    score += Math.max(0, 2 - distance * 20);
  }
  return score;
};

const findNeedleInParts = (parts, needle) => {
  if (!needle) return null;
  let glued = "";
  const gluedSpans = [];
  let spaced = "";
  const spacedSpans = [];
  parts.forEach((part) => {
    const gluedStart = glued.length;
    glued += part.norm;
    gluedSpans.push({
      start: gluedStart,
      end: glued.length,
      item: part.item,
    });
    if (part.norm) {
      if (spaced) spaced += " ";
      const spacedStart = spaced.length;
      spaced += part.norm;
      spacedSpans.push({
        start: spacedStart,
        end: spaced.length,
        item: part.item,
      });
    }
  });
  const search = (haystack, spans) => {
    const index = haystack.indexOf(needle);
    if (index < 0) return null;
    const end = index + needle.length;
    const matched = spans
      .filter((span) => span.end > index && span.start < end)
      .map((span) => span.item);
    return matched.length ? matched : null;
  };
  return search(glued, gluedSpans) || search(spaced, spacedSpans);
};

const expandToPrintedLine = (matched, lineItems, needle, flipped) => {
  const nameBox = unionBox(matched);
  if (!nameBox) return null;
  const kept = [...matched];
  for (const item of lineItems) {
    if (item.left < nameBox.topLeftX - 0.002) continue;
    const text = normalizeHighlightName(item.str);
    if (
      item.left >= nameBox.bottomRightX &&
      looksLikeOtherPerson(text, needle, flipped)
    ) {
      break;
    }
    kept.push(item);
  }
  for (const item of [...lineItems].reverse()) {
    if (item.right > nameBox.bottomRightX + 0.002) continue;
    const text = normalizeHighlightName(item.str);
    if (
      item.right <= nameBox.topLeftX &&
      looksLikeOtherPerson(text, needle, flipped)
    ) {
      break;
    }
    kept.push(item);
  }
  return unionBox(kept);
};

const matchNameOnLine = (line, needle, flipped) => {
  const parts = line.items.map((item) => ({
    item,
    norm: normalizeHighlightName(item.str),
  }));
  const compact = needle.replace(/\s+/g, "");
  const compactFlipped = flipped.replace(/\s+/g, "");
  const matched =
    findNeedleInParts(parts, needle) ||
    (compact !== needle ? findNeedleInParts(parts, compact) : null) ||
    (flipped ? findNeedleInParts(parts, flipped) : null) ||
    (compactFlipped && compactFlipped !== flipped
      ? findNeedleInParts(parts, compactFlipped)
      : null);
  if (!matched) return null;
  return {
    parts,
    matched,
    box: expandToPrintedLine(matched, line.items, needle, flipped),
    midY: line.midY,
  };
};

export const findNameLineBox = (items, name, hints = {}) => {
  const needle = normalizeHighlightName(name);
  if (!needle || !Array.isArray(items) || items.length === 0) return null;
  const flipped = flippedName(needle);
  const usable = items.filter(
    (item) => item && item.str && Number.isFinite(item.left)
  );
  const matches = groupItemsIntoLines(usable)
    .map((line) => matchNameOnLine(line, needle, flipped))
    .filter((entry) => entry?.box);

  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0].box;

  matches.sort((left, right) => {
    const leftScore = scoreNameLine(
      lineTextFromParts(left.parts),
      hints,
      left.midY
    );
    const rightScore = scoreNameLine(
      lineTextFromParts(right.parts),
      hints,
      right.midY
    );
    if (rightScore !== leftScore) return rightScore - leftScore;
    return left.midY - right.midY;
  });
  return matches[0].box;
};
